import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InAppReview } from '@capacitor-community/in-app-review';
import { useDispatch, useSelector } from 'react-redux';
import { isPlatform } from '@ionic/react';
import { TranslatedMindlyReviewFeature } from '../../translated-components';
import { useAnalytics } from '../../shared';
import useToast from '../../context/ToastContext/ToastContext';
import { loadMindlyReviewThunk, reviewMindlyThunk } from '../../redux/mindlyReviews/thunk';
import { isModalOpenedSelector, isReviewedSelector } from '../../redux/mindlyReviews/selector';
import { setIsModalOpened } from '../../redux/mindlyReviews/reducer';
import { useIsActiveFeature } from 'src/shared/hooks/useIsActiveFeature';

const MindlyReviewFeature: React.FC = () => {
  const dispatch = useDispatch();

  const counter = useRef(0);
  const { i18n } = useTranslation();
  const { showToast } = useToast();
  const { trackPredefinedEvent } = useAnalytics();
  const isModalOpened = useSelector(isModalOpenedSelector);
  const isReviewed = useSelector(isReviewedSelector);

  const isInterviewEnabled = useIsActiveFeature('is_nps_interview_enabled');
  const isInterviewNewVersion = useIsActiveFeature('is_nps_interview_new');

  useEffect(() => {
    if (counter.current === 0) {
      dispatch(loadMindlyReviewThunk());
      counter.current = 1;
    }
  }, []);

  if (!isModalOpened && isReviewed) {
    return null;
  }

  const handleModalAppeared = () => {
    trackPredefinedEvent('NPS_SCREEN_VIEWED');
  };

  const handleRateSubmited = async (rate: number) => {
    try {
      trackPredefinedEvent('NPS_RATING_STARS_SELECTED', { rating_value: rate });
      const response = await dispatch(reviewMindlyThunk({ rate })).unwrap();
      if (!response || !response.data || response.error) {
        throw new Error(response?.error?.message ?? 'Something went wrong');
      }
      return response.data;
    } catch (error) {
      console.error(`handleRateSubmited error: ${(error as Error).message}`);
      await dispatch(setIsModalOpened(false));
      setTimeout(() => {
        showToast({
          message: (error as Error).message,
        });
      }, 300);
      return null;
    }
  };

  const handleCommentModalAppeared = () => {
    trackPredefinedEvent('NPS_FEEDBACK_SCREEN_VIEWED');
  };

  const handleCommentSubmited = async (rate: number, comment: string) => {
    try {
      trackPredefinedEvent('NPS_FEEDBACK_COMPLETED', { rating_value: rate, text_feedback_provided: !!comment });
      if (comment) {
        const response = await dispatch(reviewMindlyThunk({ comment })).unwrap();
        if (!response || !response.data || response.error) {
          throw new Error(response?.error?.message ?? 'Something went wrong');
        }
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error(`handleCommentSubmited error: ${(error as Error).message}`);
      await dispatch(setIsModalOpened(false));
      setTimeout(() => {
        showToast({
          message: (error as Error).message,
        });
      }, 300);
      return null;
    }
  };

  const handlePromocodeModalAppeared = () => {
    trackPredefinedEvent('NPS_INTERVIEW_SCREEN_VIEWED');
  };

  const handleInterviewSubmited = () => {
    trackPredefinedEvent('NPS_INTERVIEW_SCREEN_SUBMITTED');
    dispatch(setIsModalOpened(false));
  };

  const handleSimpleClose = () => {
    dispatch(setIsModalOpened(false));
  };

  const handleTriggerStoreReview = async () => {
    if (isPlatform('hybrid')) {
      await InAppReview.requestReview();
    }
    dispatch(setIsModalOpened(false));
  };

  return (
    <>
      <TranslatedMindlyReviewFeature
        isOpen={isModalOpened}
        locale={i18n.language}
        linkMapper={{
          uk: 'https://calendar.app.google/zDNBR5q9fdrDHALU8',
          pl: 'https://calendar.app.google/zDNBR5q9fdrDHALU8',
          es: 'https://calendar.app.google/BEfU1XR1enGaCPCZ8',
        }}
        isInterviewEnabled={isInterviewEnabled}
        isInterviewNewVersion={isInterviewNewVersion}
        isCommentRequired={false}
        onModalAppeared={handleModalAppeared}
        onRateSubmited={handleRateSubmited}
        onCommentModalAppeared={handleCommentModalAppeared}
        onCommentSubmited={handleCommentSubmited}
        onTriggerStoreReview={handleTriggerStoreReview}
        onPromocodeModalAppeared={handlePromocodeModalAppeared}
        onInterviewSubmited={handleInterviewSubmited}
        onModalClose={handleSimpleClose}
      />
    </>
  );
};

export default MindlyReviewFeature;
